/**
 * Flashka Store — in-memory reactive data layer (Zustand).
 *
 * ============================================================================
 * ARCHITECTURE (the "Linear Pattern")
 * ============================================================================
 *
 * All data lives in memory for instant reads. The local database (IndexedDB
 * on web, SQLite on React Native) is a write-through persistence layer.
 *
 *   React components ──read──▶ Zustand (in-memory, synchronous)
 *         │                          │
 *         └──mutate──▶ Zustand ──fire-and-forget──▶ SyncStorage (persist)
 *                        │
 *                        └──subscription──▶ DO sync hook (push to server)
 *
 * ============================================================================
 * ENTITIES
 * ============================================================================
 *
 * Generic entities (folders, settings) are defined via `defineSyncEntity()`.
 * Their slices are auto-generated from the entity config and wired in
 * automatically from the client entity registry.
 *
 * Complex entities (decks, quiz) have manual slices in src/sync/slices/.
 */

import { getSyncClientCapabilities } from '@flashka/shared/sync-engine/framework/client-capabilities';
import { partitionDecksByWarmth } from '@flashka/shared/sync-engine/schema/cold-deck';
import { isValidDeckMetaRow } from '@flashka/shared/sync-engine/schema/deck-meta-guard';
import type { SyncCardProps as CardProps } from '@flashka/shared/sync-engine/schema/shared-types';
import { quizKey } from '@flashka/shared/sync-engine/schema/shared-types';

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import { DECK_STUDY_SUMMARY_TABLE } from '../../../sync-engine/framework/local-tables';
import { whenAppReady } from '../../app-ready';
import {
  createSummaryPublisher,
  DECK_SUMMARY_BACKFILL_SETTINGS_KEY,
  isTrustedSummaryRow,
  recomputeSummaryFromDisk,
} from '../../deck-summary/disk-recompute';
import { DECK_STUDY_SUMMARY_VERSION } from '../../deck-summary/summary';
import type { DeckProperties, SyncStorage } from '../../provider/sync-storage';
import type { StoredCard, StoredDeckSummary } from '../../provider/sync-storage';
import { getSyncStorage } from '../../provider/sync-storage';
import {
  loadDecksFromDisk,
  registerDiskLoadStore,
  yieldToEventLoop,
} from '../deck-disk-load';
import { deckBucketComplete, deckCardsResident } from '../deck-residency';
import { discardPendingBulk } from '../entities/bulk-coalesce';
import { clientEntities, type GenericEntityState } from '../entities/client-entities';
import { perfLog, perfNow } from '../perf-log'; // [perf-probe]
import {
  getUnsyncedDeckTracker,
  UNSYNCED_DECKS_SETTINGS_KEY,
} from '../sync-bookkeeping/unsynced-decks';
import {
  normalizeDeckCardRows,
  persistRepairedRows,
  readDeckCardRowsFromDisk,
} from './card-rows';
import { createDeckSlice, type DeckSlice } from './deck-slice';
import { progressFloorGuard } from './progress-floor';
import { reportSyncStorageError, safePut } from './store-utils';

// Generic entities loaded in Stage 1 — small (hundreds of rows total)
// and required to render the dashboard. The rest of the registry is
// loaded in Stage 4 after the dashboard is interactive.
const STAGE_1_ENTITY_NAMES = new Set([
  'folders',
  'globalSrSettings',
  'userInfo',
  'exploreProgress',
  'dashboardWidgets',
  // Consent-first social gate. MUST hydrate before `isHydrated` flips: the
  // age gate / Complete-Profile throttles read-modify-write this value the
  // moment the app is interactive, so a stage-4 (post-hydration) load lets
  // the first render see the `initial` (birthYear:null) and clobber the real
  // persisted value back to null. Tiny (one row), so stage-1 cost is nil.
  'socialConsent',
  // The user's study-group memberships. A handful of rows, but every
  // "is this deck still live in a group?" read depends on them — notes
  // routing, the SG context host, the archive gate, the Shared badge. Left
  // in stage 4 they land AFTER the first render, so a shared deck opened
  // cold read as personal for a beat, then flipped to group.
  'groupMemberships',
  // The dashboard To-Do widget's content: its own to-dos plus the calendar
  // tasks it merges into the same list. `dashboardWidgets` above decides the
  // widget IS on screen at first paint, so its rows must be too — left in
  // stage 4 they waited for the slowest tail entity (annotations, quiz
  // progress), and every cold load showed the widget's EMPTY state for
  // 0.8–2.5s before the tasks popped in (measured 2026-09-24). Same class of
  // bug as `groupMemberships`, same fix. A few dozen rows at most.
  'todos',
  'calendarTasks',
]);

// `cards` and `deckProperties` are loaded manually in stage 1 (see the
// `storage.getAll<StoredCard>('cards')` block) for the critical-path
// library render. They are NOT entity-hydrated in either stage:
//   - Stage 1's entity loop would duplicate the work.
//   - Stage 4's entity hydrate would overwrite stage 1's manual result.
// Without this exclusion, store.cards/deckProperties end up empty because
// stage 4 runs after stage 1 and the nested-record hydrate produces a
// different (and in practice empty) shape than the manual path. Confirmed
// 2026-05-20 via devtools: Cards: 0 / Decks: 0 with 3397 cards in Dexie.
const MANUALLY_HYDRATED_NAMES = new Set(['cards', 'deckProperties']);

/**
 * Lazy hydration's hot set: how many of the most recently studied decks load
 * from disk right after first paint (besides every planned deck and every
 * deck with an unsynced edit, which are always loaded).
 */
export const HOT_SET_RECENT_DECKS = 8;

/**
 * The hot set's memory bound: recently studied decks are added (most recent
 * first) only while the resident rows of everything chosen — each deck's
 * summary `rowCount` — stay within this many cards. Eight recent 5k-card decks
 * used to put 40k card bodies in memory at every launch. Planned decks are
 * always loaded whatever their size, and at least one deck always is; a
 * recent deck left out simply loads from disk when it is opened.
 */
export const HOT_SET_CARD_BUDGET = 6000;

/**
 * How long the hot set waits for the first screen to paint before preloading
 * anyway. Long enough for a cold start to get the navigator up, short enough
 * that a surface which never signals (a deep link into a deck, a headless
 * test) still warms its decks while the user is reading the first screen.
 */
export const HOT_SET_START_MAX_WAIT_MS = 2500;

/**
 * The decks worth having resident before the user asks — planned decks (the
 * exam-plan to-do branch reads card bodies) and the most recently studied.
 * Never an archived deck (server-first on open), never one already resident.
 */
export function selectHotDeckIds(
  state: Pick<
    FlashkaState,
    'deckProperties' | 'deckSummaries' | 'archivedDeckIds' | 'loadedDeckIds' | 'cards'
  >,
): string[] {
  const wanted = (id: string) =>
    state.deckProperties[id] !== undefined &&
    !state.archivedDeckIds.has(id) &&
    !deckCardsResident(state, id);
  // Rows the deck puts in memory: its summary's `rowCount` (every row, the
  // residency yardstick), else the meta's count for a deck never summarised.
  const rows = (id: string) =>
    state.deckSummaries[id]?.rowCount ?? state.deckProperties[id]?.cardCount ?? 0;
  const out = new Set<string>();
  let resident = 0;
  for (const [id, props] of Object.entries(state.deckProperties)) {
    if (props.examPlan && wanted(id)) {
      out.add(id);
      resident += rows(id);
    }
  }
  const recent = Object.keys(state.deckSummaries)
    .filter((id) => (state.deckSummaries[id]?.lastStudiedAtMs ?? 0) > 0 && wanted(id))
    .sort(
      (a, b) =>
        state.deckSummaries[b].lastStudiedAtMs - state.deckSummaries[a].lastStudiedAtMs,
    );
  // A planned deck among the recent ones still takes its slot of the N.
  for (let i = 0; i < recent.length && i < HOT_SET_RECENT_DECKS; i += 1) {
    const id = recent[i];
    if (out.has(id)) continue;
    const n = rows(id);
    // Strictly by recency: the first deck that does not fit ends the set,
    // rather than older small decks filling in behind it.
    if (out.size > 0 && resident + n > HOT_SET_CARD_BUDGET) break;
    out.add(id);
    resident += n;
  }
  return [...out];
}

/**
 * One-time backfill of study summaries for decks the bridge's boot pass cannot
 * reach — those without a resident, complete bucket (cold decks under eager
 * hydration; almost every deck under lazy hydration on the first launch after
 * the update). One deck's rows in memory at a time, a yield between decks, the
 * marker written LAST so an interrupted pass reruns. Also reruns after a
 * summary version bump (the rows carry their version).
 */
async function backfillDeckSummaries(
  storage: SyncStorage,
  store: { getState: () => FlashkaState },
  userId: string,
): Promise<void> {
  const marker = await storage.getByKey<{ key: string; value?: { version?: number } }>(
    'settings',
    DECK_SUMMARY_BACKFILL_SETTINGS_KEY,
  );
  if (marker?.value?.version === DECK_STUDY_SUMMARY_VERSION) return;
  const t0 = perfNow(); // [perf-probe]
  let recomputed = 0;
  const initial = store.getState();
  // ONE store tick per batch, not per deck: every tick re-renders Home, and
  // this walks every deck the account has (see `createSummaryPublisher`).
  const publish = createSummaryPublisher({ store, storage });
  for (const id of Object.keys(initial.deckProperties)) {
    const state = store.getState();
    if (!state.isHydrated || state.currentUserId !== userId) return;
    if (isTrustedSummaryRow(state.deckSummaries[id])) continue;
    if (deckBucketComplete(state, id)) continue; // the bridge's boot pass owns it
    try {
      const outcome = await recomputeSummaryFromDisk(id, { store, storage, publish });
      if (outcome.result === 'written') recomputed += 1;
    } catch (err) {
      reportSyncStorageError(err, 'deck-summary-backfill');
    }
    publish.flushIfDue();
    await yieldToEventLoop();
  }
  // Before the marker: the marker says every row is in, so it goes LAST.
  publish.flush();
  const final = store.getState();
  if (!final.isHydrated || final.currentUserId !== userId) return;
  safePut(storage, 'settings', {
    key: DECK_SUMMARY_BACKFILL_SETTINGS_KEY,
    value: { version: DECK_STUDY_SUMMARY_VERSION },
  });
  perfLog('startup:deck-summary backfill', t0, () => ({ recomputed })); // [perf-probe]
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

// GenericEntityState is auto-derived from client-entities.ts via InferSlice.
// No manual interface needed — add an entity there and the type updates.

interface CoreSlice {
  isHydrated: boolean;
  /**
   * Every entity has been read from disk — the stage-4 tail (quiz history,
   * annotations, mind maps, …) as well as stage 1. `isHydrated` flips before
   * that tail lands, so until this is true those entities can read as ABSENT
   * while they exist on disk. A flow that moves or deletes them (deck merge)
   * must wait for it. Stays false when the tail fails.
   */
  isEntitiesHydrated: boolean;
  /**
   * True once the stage-4 tail (quizProgress, annotations, overlays…) has
   * landed from disk. `isHydrated` flips at stage 1, BEFORE these, so a screen
   * that renders "not found" off an empty `quizProgress` has to wait for this
   * one instead — otherwise every cold open of a retake flashes "No quiz found".
   * Unlike `isEntitiesHydrated` it ALSO flips when the tail fails (a reader must
   * not hang; the engine repopulates from the server diff) — so it is a
   * "stop waiting" signal, never a "safe to move/delete" one.
   */
  isStage4Hydrated: boolean;
  /**
   * True once THIS session's UserDo handshake has answered (`sync-diff`
   * applied, or `sync-in-sync`: the cache is the server's). Until then
   * `groupMemberships` is whatever disk hydrated — after a group was deleted
   * or left elsewhere, a stale live row — so nothing may mint a group mirror
   * off it. In memory only: cleared at every handshake start and on reset.
   */
  membershipsConfirmed: boolean;
  currentUserId: string | null;

  hydrate: (userId: string) => Promise<void>;
  reset: () => void;
}

// Quiz custom mutations still called directly via useFlashkaStore.getState()
// from src/lib/quiz-utils/quiz-hooks.ts. These bypass the sync.quizzes
// extension surface (which now covers the same operations via `ops`) and
// can be migrated in a follow-up.
interface QuizExtensions {
  updateQuizName: (deckId: string, quizTs: string, name: string) => void;
  deleteQuizProgress: (deckId: string, quizTs: string) => void;
}

export type FlashkaState = GenericEntityState & QuizExtensions & DeckSlice & CoreSlice;

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useFlashkaStore = create<FlashkaState>()(
  devtools(
    // Every write — slice mutators and direct `setState` alike — passes the
    // deck progress-reset floor (see `progress-floor.ts`).
    progressFloorGuard((...args): FlashkaState => {
      const [set, get] = args;

      // Auto-generate slices from the entity registry
      let genericSlice: Record<string, unknown> = {};
      for (const entity of clientEntities) {
        genericSlice = { ...genericSlice, ...entity.createSlice(set) };
      }

      // ── Hydration ────────────────────────────────────────────────────────

      /** The hydration pass in flight, so a second call for the SAME user
       *  joins it instead of starting a second one. */
      let hydrateInFlight: { userId: string; promise: Promise<void> } | null = null;

      /** One hydration pass. The entry point is `hydrate` below — never call
       *  this directly, or two passes can interleave. */
      const runHydrate = async (userId: string): Promise<void> => {
        const state = get();
        if (state.isHydrated && state.currentUserId === userId) return;
        if (state.currentUserId && state.currentUserId !== userId) {
          get().reset();
        }

        const storage = getSyncStorage();
        const stage1Entities = clientEntities.filter(
          (e) => STAGE_1_ENTITY_NAMES.has(e.name) && !MANUALLY_HYDRATED_NAMES.has(e.name),
        );
        const stage4Entities = clientEntities.filter(
          (e) =>
            !STAGE_1_ENTITY_NAMES.has(e.name) && !MANUALLY_HYDRATED_NAMES.has(e.name),
        );

        // ── Stage 1: critical-path reads (blocks isHydrated) ─────────────
        //
        // Folders + settings + userInfo (small), deckProperties, the per-deck
        // study summaries and — EAGER hydration only — every card from
        // `cards_v2`. Under `lazyCardHydration` no card row is read here: the
        // dashboards read summaries, and a deck's bodies load from disk when
        // it is opened (`deck-disk-load.ts`), the hot set right after first
        // paint (stage 1.5 below). The one exception is a deck with an
        // unsynced local edit, force-loaded in this stage so the socket's
        // first flush — which walks resident buckets only — can push it.
        const lazy = getSyncClientCapabilities().lazyCardHydration;
        const t1 = Date.now();
        const perfT1 = perfNow(); // [perf-probe]
        const [
          stage1Results,
          allProps,
          allCards,
          activityEntry,
          summaryRows,
          unsyncedEntry,
        ] = await Promise.all([
          Promise.all(stage1Entities.map((e) => e.hydrate(storage))),
          storage.getAll<DeckProperties>('deckProperties'),
          // Eager: the dominant cold-start cost on a big account — every card
          // row in the table is read AND decoded here (cold decks' rows
          // included; the warm filter below runs after the decode).
          lazy
            ? Promise.resolve([] as StoredCard[])
            : storage.getAll<StoredCard>('cards').then((rows) => {
                perfLog('startup:hydrate-stage1 cards read+decode', perfT1, () => ({
                  rows: rows.length,
                })); // [perf-probe]
                return rows;
              }),
          storage.getByKey<{ key: string; value: Record<string, string> }>(
            'settings',
            'deckActivityMap',
          ),
          // Per-deck study summaries (client-local, ~one small row per deck).
          // Loaded whole so the counts of a deck whose bodies are NOT resident
          // (cold today, lazily hydrated later) are known before any card is.
          storage.getAll<StoredDeckSummary>(DECK_STUDY_SUMMARY_TABLE),
          // Decks with local card edits the reconnect flush still owes the
          // server (`sync-bookkeeping/unsynced-decks.ts`).
          storage.getByKey<{ key: string; value: string[] }>(
            'settings',
            UNSYNCED_DECKS_SETTINGS_KEY,
          ),
        ]);
        const perfGroup0 = perfNow(); // [perf-probe]
        getUnsyncedDeckTracker().load(
          Array.isArray(unsyncedEntry?.value) ? unsyncedEntry.value : [],
        );
        const deckSummaries: Record<string, StoredDeckSummary> = {};
        for (const row of summaryRows) {
          if (row && typeof row.deckId === 'string' && row.deckId.length > 0) {
            deckSummaries[row.deckId] = row;
          }
        }
        // Cold-deck cache: the persisted per-deck activity map. Empty until
        // the first cold-deck-aware sync-diff lands it.
        const deckActivity: Record<string, string> = activityEntry?.value ?? {};

        const stage1State: Record<string, unknown> = {};
        stage1Entities.forEach((e, i) => {
          stage1State[e.stateKey] = stage1Results[i] ?? e.initial;
        });

        // Drop malformed deckProperties rows while keying the map
        // (drop-only, no healing). Two legacy shapes can be sitting in
        // local storage: the pre-5.3b `DODeckMeta` envelope (delivered
        // verbatim by the server's unvalidated diff path) and the id-less
        // `{ updatedAtISO }` minimal meta minted by old `setDeckFromSync`.
        // Un-guarded, either would key the map as
        // `deckProperties["undefined"]` — hiding the deck and crashing
        // id-reading consumers. Both persist at pk '' on RN (the router
        // coerces a missing id to ''), so after dropping we delete that
        // stranded row to stop it re-hydrating (and re-warning) every boot.
        const deckProperties: Record<string, DeckProperties> = {};
        let droppedProps = 0;
        for (const p of allProps) {
          if (!isValidDeckMetaRow(p)) {
            droppedProps++;
            continue;
          }
          deckProperties[p.id] = p;
        }
        if (droppedProps > 0) {
          try {
            storage.removeByKey('deckProperties', '');
            console.warn(
              `[SyncStorage] dropped ${droppedProps} malformed deckProperties row(s) at hydrate`,
            );
          } catch (err) {
            reportSyncStorageError(err, 'hydrate-deckprops-guard');
          }
        }

        // Group cards from `cards_v2` by deckId for the normalised store shape
        // and normalise each deck through `normalizeDeckCardRows` — the SAME
        // path the per-deck disk loader uses, so a row reads identically
        // whichever way it enters memory (a heal applied by one reader and
        // not the other would re-push the deck on the next dirty scan).
        // Rows it repaired (healed `updatedAtISO`, assigned `sortKey`) are
        // persisted back below so the repair sticks across loads.
        // Cold-deck cache: partition the deck universe into warm (kept in
        // RAM) and archived (cold — bodies withheld). A deck with NO activity
        // entry is treated as warm (fail-safe), so until the first cold-deck
        // sync-diff populates `deckActivity` this loads everything, exactly
        // as before — making the rollout a no-op for current users.
        const allDeckIds = new Set<string>(Object.keys(deckProperties));
        for (const c of allCards) if (c.deckId) allDeckIds.add(c.deckId);
        // Lazy: no rows were read, so the summaries stand in for "decks that
        // have rows" — bookkeeping stays complete for a deck with rows but
        // no meta (it cannot render anyway).
        if (lazy) for (const id of Object.keys(deckSummaries)) allDeckIds.add(id);
        const { warm: warmDeckIds, archived: archivedDeckIds } = partitionDecksByWarmth(
          allDeckIds,
          deckActivity,
          Date.now(),
        );

        const cards: Record<string, Record<string, CardProps>> = {};
        const repairedRows: StoredCard[] = [];
        {
          const byDeck: Record<string, StoredCard[]> = {};
          for (const s of allCards) {
            if (!s.deckId) continue;
            // Skip cold decks — don't hold their cards in memory. They remain
            // in IndexedDB (disk) and re-load on demand when opened.
            if (!warmDeckIds.has(s.deckId)) continue;
            (byDeck[s.deckId] ??= []).push(s);
          }
          for (const [deckId, rows] of Object.entries(byDeck)) {
            const { map, repaired } = normalizeDeckCardRows(deckId, rows);
            cards[deckId] = map;
            for (const r of repaired) repairedRows.push(r);
          }
        }
        // Eager: every resident bucket is the deck's full set. Lazy: only the
        // decks force-loaded below are.
        const loadedDeckIds = new Set<string>(Object.keys(cards));
        // Every resident bucket was read whole from disk, so nothing local is
        // missing from memory — true even for a force-loaded deck whose disk
        // set is SHORTER than the deck.
        const diskLoadedDeckIds = new Set<string>(loadedDeckIds);
        if (lazy) {
          // Decks with local edits the reconnect flush still owes the server
          // (`sync-bookkeeping/unsynced-decks.ts`). Loaded INSIDE stage 1 —
          // the socket mounts after `isHydrated`, so its first flushLocal
          // always finds them resident. Usually zero decks.
          for (const id of getUnsyncedDeckTracker().ids()) {
            const { map, repaired } = await readDeckCardRowsFromDisk(storage, id);
            const rowCount = Object.keys(map).length;
            if (rowCount === 0) continue;
            cards[id] = map;
            diskLoadedDeckIds.add(id);
            archivedDeckIds.delete(id);
            for (const r of repaired) repairedRows.push(r);
            // `loadedDeckIds` means "the deck's COMPLETE set landed", and
            // `reconcileAllowed` derives `cardCount` from that promise. An
            // offline edit into a cold 100-card deck leaves 3 rows on this
            // device: marking that loaded made the next local add or delete
            // push `cardCount: 3` server-wide, and every device's archived
            // tile then read "3 cards". Same yardstick as the on-demand disk
            // load (`applyDeckCardsFromDisk`): an unknown count cannot
            // certify a set either.
            const expected = deckProperties[id]?.cardCount;
            if (expected == null || rowCount < expected) continue;
            loadedDeckIds.add(id);
          }
        }

        perfLog('startup:hydrate-stage1 group+repair', perfGroup0, () => ({
          warmDecks: warmDeckIds.size,
          repaired: repairedRows.length,
        })); // [perf-probe]
        set({
          ...stage1State,
          cards,
          deckProperties,
          deckActivity,
          deckSummaries,
          loadedDeckIds,
          archivedDeckIds,
          diskLoadedDeckIds,
          isHydrated: true,
          currentUserId: userId,
        });
        perfLog('startup:hydrate-stage1 total', perfT1, () => ({
          decks: Object.keys(deckProperties).length,
          cards: allCards.length,
          warm: warmDeckIds.size,
          archived: archivedDeckIds.size,
        })); // [perf-probe]
        console.log(
          `[SyncStorage] hydrate stage=1 duration_ms=${Date.now() - t1} decks=${
            Object.keys(deckProperties).length
          } cards=${allCards.length} warm=${warmDeckIds.size} archived=${archivedDeckIds.size}`,
        );

        // Persist the legacy-card repairs (healed updatedAtISO / assigned
        // sortKey) back to IndexedDB. Best-effort and NON-blocking —
        // `isHydrated` is already set above, so the UI renders immediately;
        // this just makes the in-memory repair durable so it isn't
        // recomputed (and re-pushed to the server) on the next load. This
        // is the fix for the migrated-account refresh flood.
        if (repairedRows.length > 0) {
          void (async () => {
            try {
              await persistRepairedRows(storage, repairedRows);
              console.log(
                `[SyncStorage] repaired ${repairedRows.length} legacy card(s) (updatedAtISO/sortKey) → IDB`,
              );
            } catch (err) {
              reportSyncStorageError(err, 'hydrate-card-repair');
            }
          })();
        }

        // ── Stage 4: background tail ────────────────────────────────────
        //
        // Remaining generic entities — quizProgress, annotations,
        // overlays, group memberships. Off the critical path; failure
        // here is non-fatal (engine will repopulate from server diff).
        //
        // Started but NOT awaited here: the two passes below feed Home's
        // PRIMARY numbers. On the first launch after the summary table
        // appears no deck has a row, and under lazy hydration no deck has a
        // bucket for the bridge to summarise from, so every to-do / due /
        // new count on the dashboard reads zero until the backfill lands.
        // Serialising that behind this tail made a 100-deck library show an
        // empty study queue for as long as it took. They touch different
        // tables, so they run together and the hydrate still waits for both.
        const t4 = Date.now();
        const perfT4 = perfNow(); // [perf-probe]
        const stage4Done = (async () => {
          try {
            // The socket mounts on `isHydrated` (set above), so a reconnect's
            // sync-diff or broadcast can replace a VALUE entity while these
            // reads are in flight. The disk copy was read before that frame
            // and is older by construction: a value slice that moved since
            // now is kept, or a reconnect's fresh `credits` would be replaced
            // by the persisted pre-consume one until the next credits change.
            // Value slices only: a record/collection slice is a set of rows,
            // and keeping it whole would drop the disk rows the frame lacked
            // (that case needs a per-key merge).
            const valueSlicesAtRead = new Map<string, unknown>();
            const atRead = get() as unknown as Record<string, unknown>;
            for (const e of stage4Entities) {
              if (e.type === 'value')
                valueSlicesAtRead.set(e.stateKey, atRead[e.stateKey]);
            }
            const stage4Results = await Promise.all(
              stage4Entities.map((e) => e.hydrate(storage)),
            );
            if (get().currentUserId !== userId) return;
            const now = get() as unknown as Record<string, unknown>;
            const stage4State: Record<string, unknown> = {};
            stage4Entities.forEach((e, i) => {
              if (
                valueSlicesAtRead.has(e.stateKey) &&
                now[e.stateKey] !== valueSlicesAtRead.get(e.stateKey)
              ) {
                return;
              }
              stage4State[e.stateKey] = stage4Results[i] ?? e.initial;
            });
            set({
              ...(stage4State as Partial<FlashkaState>),
              isEntitiesHydrated: get().currentUserId === userId,
              isStage4Hydrated: true,
            });
            console.log(`[SyncStorage] hydrate stage=4 duration_ms=${Date.now() - t4}`);
            perfLog('startup:hydrate-stage4', perfT4); // [perf-probe]
          } catch (err) {
            // Still "loaded" for readers: the engine repopulates from the
            // server diff, and a screen waiting on this flag must not hang.
            if (get().currentUserId === userId) set({ isStage4Hydrated: true });
            reportSyncStorageError(err, 'hydrate-stage4');
          }
        })();

        // ── Stage 1.5 (lazy only): the hot set, after first paint ───────
        //
        // Planned decks and the most recently studied load from disk one
        // deck at a time with a yield between them, so the decks the user is
        // most likely to open next are resident by the time they tap — and
        // Home has already painted from the summaries. Never bumps
        // server-side activity (preloading must not keep a deck warm).
        const stillThisUser = () => get().currentUserId === userId;
        const countsDone = (async () => {
          if (lazy) {
            // WAIT FOR THE SCREEN FIRST. This preload is speculative and the
            // first paint is not; on a cold start they used to land in the
            // same two seconds and fight over the JS thread. See `app-ready`
            // for why the timeout is the contract rather than a fallback.
            await whenAppReady(HOT_SET_START_MAX_WAIT_MS);
            if (!stillThisUser()) return;
            const perfHot = perfNow(); // [perf-probe]
            let perfSelfMs = 0; // [perf-probe]
            let perfEmpty = 0; // [perf-probe]
            const hot = selectHotDeckIds(get());
            const resident = await loadDecksFromDisk(
              hot,
              stillThisUser,
              (_id, selfMs, ok) => {
                perfSelfMs += selfMs;
                if (!ok) perfEmpty += 1;
              }, // [perf-probe]
            );
            // `selfMs` vs the span's own ms is the reading that matters: this
            // pass yields between decks, so the gap between them is other work
            // running, not this pass stalling. `empty` counts decks whose
            // summary says they were studied but that have no rows on THIS
            // device (studied elsewhere) — a disk read that can only ever come
            // back with nothing.
            perfLog('startup:hydrate-stage1.5 hot set', perfHot, () => ({
              wanted: hot.length,
              resident: resident.length,
              empty: perfEmpty,
              selfMs: Math.round(perfSelfMs),
            })); // [perf-probe]
          }

          // ── One-time summary backfill (both modes) ────────────────────
          if (!stillThisUser()) return;
          await backfillDeckSummaries(storage, { getState: get }, userId);
        })();

        await Promise.all([stage4Done, countsDone]);
      };

      return {
        // ── Generic entities (auto-generated from registry) ──────────────────
        //
        // Folders' `moveFolderToParent` / `reorderFolder` used to live here as
        // one-line wrappers around `updateFolder(id, partial)`. They're now
        // inlined into the `sync.folders` extension factory (src/sync/sync.ts),
        // which calls `ops.update` directly — zero slice glue needed.
        ...(genericSlice as unknown as GenericEntityState),

        // ── Complex entity slices (manual) ───────────────────────────────────
        ...createDeckSlice(...args),

        // ── Quiz extension methods ──────────────────────────────────────────
        updateQuizName: (deckId: string, quizTs: string, name: string) => {
          const key = quizKey(deckId, quizTs);
          const now = new Date().toISOString();
          set((s) => {
            const existing = s.quizProgress[key];
            if (!existing) return s;
            const updatedAttempts = [...existing.attempts];
            if (updatedAttempts.length > 0) {
              const last = updatedAttempts.length - 1;
              updatedAttempts[last] = { ...updatedAttempts[last], quizName: name };
            }
            return {
              quizProgress: {
                ...s.quizProgress,
                [key]: { ...existing, attempts: updatedAttempts, updatedAtISO: now },
              },
            };
          });
          const entry = get().quizProgress[key];
          if (entry) getSyncStorage().put('quizProgress', entry);
        },

        deleteQuizProgress: (deckId: string, quizTs: string) => {
          const key = quizKey(deckId, quizTs);
          set((s) => {
            const { [key]: _, ...rest } = s.quizProgress;
            return { quizProgress: rest };
          });
          getSyncStorage().removeByKey('quizProgress', [deckId, quizTs]);
        },

        // ── Core state ───────────────────────────────────────────────────────
        isHydrated: false,
        isEntitiesHydrated: false,
        isStage4Hydrated: false,
        membershipsConfirmed: false,
        currentUserId: null,

        hydrate: (userId: string) => {
          const state = get();
          if (state.isHydrated && state.currentUserId === userId)
            return Promise.resolve();
          // Two hydrates for the SAME user must be ONE pass. Nothing used to
          // stop them: an auth flap (or StrictMode's double effect) started a
          // second one, and its stage-1 `set` — which lands a freshly built
          // `cards` map as a LOCAL tick — dropped every bucket the first pass
          // had already made resident. The dirty scan then read those rows as
          // deletions and pushed `cards-bulk-delete` for them: the server
          // deleted the user's cards.
          if (hydrateInFlight?.userId === userId) return hydrateInFlight.promise;
          const promise = runHydrate(userId).finally(() => {
            if (hydrateInFlight?.promise === promise) hydrateInFlight = null;
          });
          hydrateInFlight = { userId, promise };
          return promise;
        },

        reset: () => {
          // Build initial state from registry
          const genericInitial: Record<string, unknown> = {};
          for (const entity of clientEntities) {
            genericInitial[entity.stateKey] = entity.initial;
          }

          getUnsyncedDeckTracker().reset();
          // Server frames staged for a coalesced publish belong to the user
          // being wiped; they must not land in the next user's store. Their
          // rows are already on disk (persistence is per frame).
          discardPendingBulk();
          set({
            ...genericInitial,
            cards: {},
            deckProperties: {},
            // Cold-deck cache state is LOCAL-only and not registry-backed, so
            // the generic loop above never touches it. Left behind, a user
            // switch inherited the previous account's warm/cold partition:
            // decks rendered archived, or a cold deck read as loaded.
            deckActivity: {},
            loadedDeckIds: new Set<string>(),
            archivedDeckIds: new Set<string>(),
            diskLoadedDeckIds: new Set<string>(),
            // Same for the per-deck summaries — user B must not render user A's
            // counts for the tick before hydrate.
            deckSummaries: {},
            isHydrated: false,
            isEntitiesHydrated: false,
            isStage4Hydrated: false,
            membershipsConfirmed: false,
            currentUserId: null,
          });
        },
      };
    }),
    { name: 'FlashkaStore' },
  ),
);

// The per-deck disk loader reads and writes this store; injected rather than
// imported by it so the two modules have no import cycle.
registerDiskLoadStore(useFlashkaStore);
